import { Flex, InfoBox, Select, Switch, Text } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import {
  CURRENCY_SYMBOL_POSITION_OPTIONS,
  NEGATIVE_NUMBERS_OPTIONS,
  NULL_DISPLAY_OPTIONS,
  NUMBER_SCALE_OPTIONS,
  ZERO_DISPLAY_OPTIONS,
} from "./constants";
import { DataFormatRow } from "./DataFormatRow";
import { DateFormatBuilderFields } from "./DateFormatBuilderFields";
import {
  formatDatePreview,
  formatNullPreview,
  formatNumberPreview,
  formatZeroPreview,
} from "./formatPreview";
import { DEFAULT_BUILDER_STATE, parsePattern } from "./patternBuilder";
import * as styles from "./styles_vanilla.css";
import type { DataFormatValues, DataFormatVariant } from "./types";
import type { FieldDataType } from "~/graphql/supergraphTypes";

const toNumberPreviewValues = (
  values: DataFormatValues,
  fieldType: FieldDataType | undefined,
): DataFormatValues => ({
  ...values,
  numberScale:
    fieldType === undefined || fieldType === "QUANTITY" || fieldType === "VALUE"
      ? values.numberScale
      : "as_is",
  showCurrencySymbol:
    fieldType === undefined || fieldType === "VALUE" ? values.showCurrencySymbol : false,
});

const DisabledSelect = ({
  label,
  value,
  options,
}: {
  label: string;
  value: string;
  options: ReadonlyArray<{ value: string; label: string }>;
}) => (
  <Select
    label={label}
    value={value}
    className={styles.formControl}
    onChange={() => {}}
    disabled
  >
    <Select.Section>
      {options.map((opt) => (
        <Select.Option key={opt.value} value={opt.value}>
          {opt.label}
        </Select.Option>
      ))}
    </Select.Section>
  </Select>
);

const DisabledBody = ({
  values,
  fieldType,
}: {
  values: DataFormatValues;
  fieldType?: FieldDataType;
}) => {
  const isNumeric =
    !fieldType ||
    fieldType === "QUANTITY" ||
    fieldType === "VALUE" ||
    fieldType === "PERCENTAGE";
  const showCurrencyFields = !fieldType || fieldType === "VALUE";
  const showNumberScale = !fieldType || fieldType === "QUANTITY" || fieldType === "VALUE";
  const showDateFormat = !fieldType || fieldType === "DATE";

  const nullDisplayRow = (
    <DataFormatRow
      field={
        <DisabledSelect
          label="Null display"
          value={values.nullDisplay}
          options={NULL_DISPLAY_OPTIONS}
        />
      }
      preview={<Text variant="secondary-md">{formatNullPreview(values.nullDisplay)}</Text>}
    />
  );

  const dateFormatRow = showDateFormat ? (
    <DataFormatRow
      field={
        <DateFormatBuilderFields
          state={parsePattern(values.dateFormatPattern) ?? DEFAULT_BUILDER_STATE}
          onChange={() => {}}
          disabled
          idPrefix="data-format-off"
        />
      }
      preview={
        <Text variant="secondary-md">{formatDatePreview(values.dateFormatPattern)}</Text>
      }
    />
  ) : null;

  if (!isNumeric) {
    // TEXT or DATE: null display, then optional date format
    return (
      <Flex flexDirection="column">
        {nullDisplayRow}
        {dateFormatRow}
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column">
      <DataFormatRow
        field={
          <Flex flexDirection="column" gap={SPACING.MEDIUM}>
            <DisabledSelect
              label="Negative numbers"
              value={values.negativeNumbers}
              options={NEGATIVE_NUMBERS_OPTIONS}
            />
            {showNumberScale && (
              <DisabledSelect
                label="Number scale"
                value={values.numberScale}
                options={NUMBER_SCALE_OPTIONS}
              />
            )}
            <Switch
              id="data-format-off-thousands"
              label="1,000 separators"
              labelPosition="right"
              checked={values.thousandsSeparator}
              onChange={() => {}}
              size="small"
              disabled
            />
            {showCurrencyFields && (
              <>
                <Switch
                  id="data-format-off-currency-symbol"
                  label="Show currency symbol"
                  labelPosition="right"
                  checked={values.showCurrencySymbol}
                  onChange={() => {}}
                  size="small"
                  disabled
                />
                {values.showCurrencySymbol && (
                  <DisabledSelect
                    label="Currency symbol position"
                    value={values.currencySymbolPosition}
                    options={CURRENCY_SYMBOL_POSITION_OPTIONS}
                  />
                )}
              </>
            )}
          </Flex>
        }
        previewLabel={<Text variant="secondary-md">Display preview</Text>}
        preview={
          <Text variant="secondary-md">
            {formatNumberPreview(toNumberPreviewValues(values, fieldType))}
          </Text>
        }
      />
      <DataFormatRow
        field={
          <DisabledSelect
            label="Zero display"
            value={values.zeroDisplay}
            options={ZERO_DISPLAY_OPTIONS}
          />
        }
        preview={<Text variant="secondary-md">{formatZeroPreview(values.zeroDisplay)}</Text>}
      />
      {nullDisplayRow}
    </Flex>
  );
};

/** Shown when the data-format toggle is off. For `variant="field"`, renders
 * the inherited query-level values as disabled form controls when provided;
 * falls back to the informational banner when not available. For
 * `variant="query"`, always shows the informational banner. */
export const OffState = ({
  variant,
  inheritedValues,
  fieldType,
}: {
  variant: DataFormatVariant;
  inheritedValues?: DataFormatValues;
  fieldType?: FieldDataType;
}) => {
  if (variant === "field") {
    return inheritedValues ? (
      <DisabledBody values={inheritedValues} fieldType={fieldType} />
    ) : (
      <InfoBox variant="info" title="Inheriting query defaults">
        Decimal places, currency, and date formats follow the query&apos;s display settings
      </InfoBox>
    );
  }

  return (
    <InfoBox variant="info" title="Formatting managed outside this query">
      Display settings like rounding, currency, and date formats must be set up externally
    </InfoBox>
  );
};
